import React, { useEffect, useRef, useState } from 'react';
import { CountdownSettings, fetchCountdown } from '../../services/manageApi';
import {
  CountdownStage, DEFAULT_COUNTDOWN_LABEL, POLL_MS, TICK_MS, countdownStage, formatCountdown, remainingMs, serverOffsetMs,
} from '../../utils/countdown';
import { formatDateTime, viewerTimeZone } from '../../utils/dates';
import './CountdownBanner.css';

interface DisplayProps {
  label: string | null;
  endsAt: string;
  remaining: number;
  /** Render the screen-reader live region (off for previews). */
  announce?: boolean;
}

/**
 * Screen-reader text. It changes at most once a minute, plus on every stage
 * change (warning -> critical -> done), so screen readers aren't flooded by
 * the per-second tick.
 */
function announcementFor(stage: CountdownStage, label: string, remaining: number): string {
  if (stage === 'done') return 'The countdown has ended.';
  if (stage === 'critical') return 'Less than a minute left';
  const minutesLeft = Math.ceil(remaining / 60000);
  return `${label} ${minutesLeft} minute${minutesLeft === 1 ? '' : 's'}`;
}

/** The banner itself, without data loading (also used as the settings preview). */
export const CountdownDisplay: React.FC<DisplayProps> = ({ label, endsAt, remaining, announce = true }) => {
  const stage = countdownStage(remaining);
  const done = stage === 'done';
  const shownLabel = label || DEFAULT_COUNTDOWN_LABEL;
  return (
    <section className={`countdown-banner countdown-banner--${stage}`} data-testid="countdown-banner" data-tour="countdown" aria-label="Countdown">
      <div className="countdown-banner__label">{done ? '' : shownLabel}</div>
      <div className="countdown-banner__digits" aria-hidden="true">{done ? "Time's up!" : formatCountdown(remaining)}</div>
      <div className="countdown-banner__closes">Closes {formatDateTime(endsAt, '-', viewerTimeZone())}</div>
      {announce && <span className="sr-only" aria-live="polite" data-testid="countdown-announcement">{announcementFor(stage, shownLabel, remaining)}</span>}
    </section>
  );
};

const CountdownBanner: React.FC<{ competitionId: number }> = ({ competitionId }) => {
  const [settings, setSettings] = useState<CountdownSettings | null>(null);
  const offset = useRef(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    setSettings(null); // never show the previous competition's countdown
    const load = () => {
      fetchCountdown(competitionId)
        .then((result) => {
          if (cancelled) return;
          offset.current = serverOffsetMs(result.server_time, Date.now());
          setSettings(result);
          setNow(Date.now());
        })
        .catch((err) => {
          if (!cancelled) console.error('Failed to load countdown', err);
        });
    };
    // Like the other pollers on this page: skip hidden tabs (an idle background tab would
    // otherwise hit the expired-token redirect) and catch up as soon as the tab is shown.
    const pollIfVisible = () => {
      if (document.visibilityState !== 'hidden') load();
    };
    load();
    const poll = setInterval(pollIfVisible, POLL_MS);
    document.addEventListener('visibilitychange', pollIfVisible);
    window.addEventListener('pageshow', pollIfVisible);
    return () => {
      cancelled = true;
      clearInterval(poll);
      document.removeEventListener('visibilitychange', pollIfVisible);
      window.removeEventListener('pageshow', pollIfVisible);
    };
  }, [competitionId]);

  const active = Boolean(settings?.enabled && settings.ends_at);
  useEffect(() => {
    if (!active) return undefined;
    const tick = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(tick);
  }, [active]);

  if (!active || !settings?.ends_at) return null;
  return (
    <CountdownDisplay
      label={settings.label}
      endsAt={settings.ends_at}
      remaining={remainingMs(settings.ends_at, now, offset.current)}
    />
  );
};

export default CountdownBanner;
